import { useState, useRef, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useProducts } from '@/hooks/useProducts';

interface AlternativeToSelectProps {
  value: string[];
  onChange: (slugs: string[]) => void;
  excludeSlug?: string;
}

export function AlternativeToSelect({ value, onChange, excludeSlug }: AlternativeToSelectProps) {
  const { data: products } = useProducts();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const available = (products || []).filter(
    (p) => p.slug !== excludeSlug && !value.includes(p.slug)
  );

  const filtered = search
    ? available.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
    : available;

  const selectedProducts = (products || []).filter((p) => value.includes(p.slug));

  return (
    <div ref={ref} className="relative">
      <div
        className="min-h-[36px] flex flex-wrap items-center gap-1.5 rounded-md border border-border bg-secondary px-2 py-1.5 cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        {selectedProducts.map((p) => (
          <Badge
            key={p.slug}
            className="bg-primary/10 text-primary border-primary/20 gap-1 pl-1 pr-1.5 py-0.5 text-xs"
          >
            <img src={p.logo_url} alt="" className="w-4 h-4 rounded object-contain" />
            {p.name}
            <button
              type="button"
              className="ml-0.5 hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onChange(value.filter((s) => s !== p.slug));
              }}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        {selectedProducts.length === 0 && (
          <span className="text-xs text-muted-foreground">Select alternatives…</span>
        )}
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground ml-auto shrink-0" />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-card shadow-lg max-h-48 overflow-hidden">
          <input
            type="text"
            className="w-full px-3 py-2 text-xs bg-secondary border-b border-border outline-none placeholder:text-muted-foreground"
            placeholder="Search tools…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="overflow-y-auto max-h-36">
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">No tools found</div>
            )}
            {filtered.map((p) => (
              <button
                key={p.slug}
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-secondary/80 transition-colors"
                onClick={() => {
                  onChange([...value, p.slug]);
                  setSearch('');
                }}
              >
                <img src={p.logo_url} alt="" className="w-5 h-5 rounded object-contain" />
                <span className="text-xs font-medium text-foreground">{p.name}</span>
                <span className="text-[10px] text-muted-foreground ml-auto">{p.category}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
